import Microgame from '../engine/Microgame.js';

export default class UmbrellaGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Parapluie !`, en: `Umbrella!`, es: `¡Paraguas!` },
            controls: { fr: `Glisse ton doigt/souris pour diriger le parapluie`, en: `Swipe to position the umbrella`, es: `Desliza para posicionar el paraguas` },
            objective: { fr: `Protège le personnage de la pluie acide`, en: `Protect the character from acid rain`, es: `Protege al personaje de la lluvia ácida` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M2 12a10 10 0 0 1 20 0Z"/><path d="M12 12v10"/><path d="M12 22a2 2 0 0 0 2-2"/></svg>`,
            actions: [
                { type: 'move', x: 50, y: 30, duration: 180 },
                { type: 'swipe', fromX: 50, fromY: 30, toX: 35, toY: 30, duration: 260 },
                { type: 'wait', duration: 220 },
                { type: 'swipe', fromX: 35, fromY: 30, toX: 65, toY: 30, duration: 260 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.character = null;
        this.umbrella = null;
        this.rain = [];
        this.umbrellaX = 50;
        this.charX = 50;
        this.charTargetX = 50;
        this.moveTimer = 0;
        this.rainStartTimer = 0.5;
        this.rainSpeed = 200;

        this.speedMult = engine.speedMultiplier || 1.0;

        // Formula: maxTime = duration / speedMult. 
        // For maxTime to be 4s at x1, and 2.5s at x4:
        // adjustedSpeed(1) = 1. adjustedSpeed(4) = 4000 / 2500 = 1.6
        // 1 + 3 * x = 1.6 => x = 0.2
        const adjustedSpeed = 1 + (this.speedMult - 1) * 0.2;
        this.duration = 4000 * (this.speedMult / adjustedSpeed);

        this.rainChance = 0.08 + (this.speedMult - 1) * 0.05; // 0.08 to 0.23 chance per tick
        this.charSpeedMult = 2.0 + (this.speedMult - 1) * 0.6; // 2.0 to 3.8 scaling, faster base speed
    }

    setup() {
        this.engine.ui.instruction.textContent = "PROTECT!";

        // Character (Primary Color - Safe)
        this.character = document.createElement('div');
        this.character.style.position = 'absolute';
        this.character.style.width = '30px';
        this.character.style.height = '50px';
        this.character.style.background = this.primaryColor; // Changed from white
        this.character.style.bottom = '0';
        this.character.style.left = '50%';
        this.character.style.transform = 'translateX(-50%)';
        this.character.style.borderRadius = '10px 10px 0 0';
        this.character.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.container.appendChild(this.character);

        // Eyes (Secondary Color - Contrast)
        const eye = document.createElement('div');
        eye.style.position = 'absolute';
        eye.style.width = '4px';
        eye.style.height = '4px';
        eye.style.background = this.secondaryColor; // Changed from black
        eye.style.borderRadius = '50%';
        eye.style.top = '10px';
        eye.style.left = '8px';
        this.character.appendChild(eye);

        const eye2 = eye.cloneNode();
        eye2.style.left = '18px';
        this.character.appendChild(eye2);

        // Umbrella (Primary Color - Safe)
        this.umbrella = document.createElement('div');
        this.umbrella.style.position = 'absolute';
        this.umbrella.style.width = '120px';
        this.umbrella.style.height = '15px';
        this.umbrella.style.background = this.primaryColor;
        this.umbrella.style.borderRadius = '60px 60px 0 0';
        this.umbrella.style.bottom = '60px';
        this.umbrella.style.left = '50%';
        this.umbrella.style.transform = 'translateX(-50%)';
        this.umbrella.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.container.appendChild(this.umbrella);

        // Handle (Primary Color)
        const handle = document.createElement('div');
        handle.style.position = 'absolute';
        handle.style.width = '4px';
        handle.style.height = '40px';
        handle.style.background = this.primaryColor; // Changed from white
        handle.style.bottom = '-40px';
        handle.style.left = '50%';
        handle.style.transform = 'translateX(-50%)';
        this.umbrella.appendChild(handle);

        this.onMove = this.onMove.bind(this);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
    }

    update(dt) {
        if (!this.active || this.won) return;

        if (this.engine.timer <= 100) {
            this.win();
            return;
        }

        // Move Character
        this.moveTimer -= dt;
        if (this.moveTimer <= 0) {
            this.charTargetX = 20 + Math.random() * 60;
            // Shorter base rest periods overall, decreasing further with difficulty
            const baseWait = Math.max(150, 600 - (this.speedMult * 120));
            this.moveTimer = baseWait + Math.random() * baseWait;
        }

        const diff = this.charTargetX - this.charX;
        if (Math.abs(diff) > 0.5) {
            // Faster movement at higher difficulties
            this.charX += diff * this.charSpeedMult * (dt / 1000);
            this.character.style.left = `${this.charX}%`;
        }

        // Rain Delay
        if (this.rainStartTimer > 0) {
            this.rainStartTimer -= dt / 1000;
            return;
        }

        // Spawn Rain (Secondary Color - Danger)
        if (Math.random() < this.rainChance) {
            this.createRain();
        }

        // Move Rain
        const umbrellaTopY = 87;
        const charTopY = 92;

        for (let i = this.rain.length - 1; i >= 0; i--) {
            const drop = this.rain[i];
            const prevY = drop.y;
            drop.y += this.rainSpeed * (dt / 1000);
            drop.el.style.top = `${drop.y}%`;

            // Continuous Collision Detection for Umbrella
            if (prevY <= umbrellaTopY && drop.y >= umbrellaTopY) {
                if (Math.abs(drop.x - this.umbrellaX) < 18) {
                    drop.el.remove();
                    this.rain.splice(i, 1);

                    const pop = document.createElement('div');
                    pop.style.position = 'absolute';
                    pop.style.width = '6px';
                    pop.style.height = '6px';
                    pop.style.background = this.primaryColor; // Changed from white
                    pop.style.borderRadius = '50%';
                    pop.style.left = `${drop.x}%`;
                    pop.style.top = `${umbrellaTopY}%`;
                    this.container.appendChild(pop);
                    setTimeout(() => pop.remove(), 100);
                    continue;
                }
            }

            // Collision with Character
            if (drop.y > charTopY) {
                if (Math.abs(drop.x - this.charX) < 4) {
                    this.lose();
                    this.character.style.background = this.secondaryColor; // Turn to danger color
                }
            }

            if (drop.y > 100) {
                drop.el.remove();
                this.rain.splice(i, 1);
            }
        }
    }

    createRain() {
        const drop = document.createElement('div');
        drop.style.position = 'absolute';
        drop.style.width = '4px';
        drop.style.height = '15px';
        drop.style.background = this.secondaryColor;
        const target = this.charX + (Math.random() * 40 - 20);
        drop.style.left = `${target}%`;
        drop.style.top = '-10%';
        this.container.appendChild(drop);
        this.rain.push({ el: drop, x: target, y: -10 });
    }

    onMove(e) {
        if (!this.active || this.won) return;
        const rect = this.container.getBoundingClientRect();
        this.umbrellaX = (e.clientX - rect.left) / rect.width * 100;
        this.umbrella.style.left = `${this.umbrellaX}%`;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
    }
}
